import { useState, useEffect, useCallback, useMemo } from 'react';

import {
    getCheckInConfigs,
    createCheckInConfig,
    updateCheckInConfig,
    deleteCheckInConfig,
    type CheckInConfigDto,
    type CheckInConfigPayload,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
    facilities: { FacilityCode: string; DisplayName: string }[];
}

/**
 * DRIVER CHECK-IN admin (dock.CheckInConfig, migration 140).
 *
 * Pete, 2026-08-17: "always want this in a screen so it can be configured, not
 * in code that has to be rebuilt if we change our minds later." This is that
 * screen. Everything the gate does that is a judgement rather than a mechanism
 * is here, and none of it needs a deploy to change.
 *
 * A facility with NO row still works: the tablet runs the shipped defaults,
 * exactly as a board with no BoardConfig does. Nothing here is required.
 *
 * ⚠️ THE MATCHING SECTION IS NOT COSMETIC. It decides whether a truck is
 * attached to a load with no human in the loop. The copy in it is written for
 * the person who will change it at 6am with drivers waiting, not for whoever
 * wrote the code.
 */

/** The four things a driver can be recognised by, in the order they ship in. */
const SIGNALS = [
    { id: 'phone',     label: 'Cell phone number',  hint: 'The one thing the driver carries and cannot misremember.' },
    { id: 'container', label: 'Container number',   hint: 'Identifies one physical box.' },
    { id: 'reference', label: 'Symbia reference',   hint: 'Our own number from the confirmation.' },
    { id: 'po',        label: 'PO number',          hint: 'A PO often covers several trucks in a day — weak on its own.' },
] as const;
type SignalId = typeof SIGNALS[number]['id'];

const DL_MODES: { value: CheckInConfigDto['dlCaptureMode']; label: string }[] = [
    { value: 'off',      label: 'Don’t take a photo' },
    { value: 'optional', label: 'Take a photo when the tablet can (recommended)' },
    { value: 'required', label: 'Require a photo — no photo, no check-in' },
];

const BLANK: CheckInConfigPayload = {
    facilityCode: '',
    configKey: '',
    displayName: '',
    isDefault: false,
    isActive: true,
    matchSignalOrder: 'phone,container,reference,po',
    confidentSignals: 'phone,container,reference',
    phoneMinDigits: 10,
    phoneMatchDigits: 10,
    multiMatchBehavior: 'ask',
    collectContainerNumber: true,
    collectTrailerNumber: true,
    collectPoNumber: true,
    /* ⭐ TRUE, matching migration 192's `DEFAULT (1)` and `CHECKIN_CONFIG_DEFAULTS`
       on the server. A new config created from this screen must behave the same as
       one created by the migration's default, or "add a facility" would quietly
       turn a feature off that every existing facility has on. */
    collectPalletCount: true,
    collectLanguage: true,
    requireFoodSafetyAck: true,
    foodSafetyWordingVer: 'v1',
    foodSafetyText: null,
    dlCaptureMode: 'optional',
    dlRetentionDays: null,
    autoArriveOnConfident: true,
    requireDoorOnArrive: false,
    autoReleaseDoorOnDeparted: true,
    walkInCreatesRequest: true,
    maxCheckInsPerMinute: 12,
    duplicateWindowMinutes: 10,
    idleResetSeconds: 90,
    /* ⭐ #73 — NULL, NOT A NUMBER. A new configuration inherits the general
       timeout on every screen, which is exactly how every existing row behaves.
       Seeding these with 30 / 120 / 5 would write three decisions nobody made
       into every site created from now on. */
    idleVehicleSeconds: null,
    idleSafetySeconds: null,
    doneResetSeconds: null,
    kioskTruckDays: 2,
    doorIdleAlertMinutes: 15,
};

function checkInUrl(configKey: string): string {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/checkin?cfg=${encodeURIComponent(configKey)}`;
}

const parseList = (csv: string): SignalId[] =>
    csv.split(',').map(s => s.trim().toLowerCase())
        .filter((s): s is SignalId => SIGNALS.some(x => x.id === s));

export default function CheckInConfigAdmin({ getToken, facilities }: Props) {
    const [rows, setRows] = useState<CheckInConfigDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    /** null = not editing; number = editing that id; 'new' = creating. */
    const [editing, setEditing] = useState<number | 'new' | null>(null);
    const [draft, setDraft] = useState<CheckInConfigPayload>(BLANK);
    const [copied, setCopied] = useState<string | null>(null);
    const [deleteArmed, setDeleteArmed] = useState<number | null>(null);

    const facilityName = useMemo(() => {
        const m = new Map(facilities.map((f) => [f.FacilityCode, f.DisplayName]));
        return (code: string | null) => (code ? m.get(code) ?? code : '—');
    }, [facilities]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { configs } = await getCheckInConfigs(await getToken());
            setRows(configs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the check-in settings.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { void load(); }, [load]);

    const order = parseList(draft.matchSignalOrder);
    const trusted = parseList(draft.confidentSignals);
    const unused = SIGNALS.map(s => s.id).filter(id => !order.includes(id));

    const setOrder = (next: SignalId[]) => setDraft(d => ({
        ...d,
        matchSignalOrder: next.join(','),
        // A signal that is no longer used cannot stay trusted — the server drops
        // it anyway, and a checkbox that says otherwise would be a lie.
        confidentSignals: parseList(d.confidentSignals).filter(s => next.includes(s)).join(','),
    }));

    const move = (id: SignalId, delta: number) => {
        const next = [...order];
        const i = next.indexOf(id);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setOrder(next);
    };

    const toggleUse = (id: SignalId, use: boolean) =>
        setOrder(use ? [...order, id] : order.filter(s => s !== id));

    const toggleTrust = (id: SignalId, trust: boolean) => setDraft(d => ({
        ...d,
        confidentSignals: (trust ? [...trusted, id] : trusted.filter(s => s !== id)).join(','),
    }));

    function startNew() {
        setDraft({ ...BLANK, facilityCode: facilities[0]?.FacilityCode ?? '' });
        setEditing('new');
        setNotice(null);
        setError(null);
    }

    function startEdit(c: CheckInConfigDto) {
        setDraft({
            facilityCode: c.facilityCode ?? '',
            configKey: c.configKey ?? '',
            displayName: c.displayName,
            isDefault: c.isDefault,
            isActive: c.isActive,
            matchSignalOrder: c.matchSignalOrder,
            confidentSignals: c.confidentSignals,
            phoneMinDigits: c.phoneMinDigits,
            phoneMatchDigits: c.phoneMatchDigits,
            multiMatchBehavior: c.multiMatchBehavior,
            collectContainerNumber: c.collectContainerNumber,
            collectTrailerNumber: c.collectTrailerNumber,
            collectPoNumber: c.collectPoNumber,
            /* ⚠️ `?? true`, NOT a bare read. A config fetched from a backend that has
               not shipped the 2026-09-24 repository fix arrives without this key, and
               an editor that loaded `undefined` into a required boolean would post it
               back as a change nobody made. The fallback is the migration's default,
               so an unknown value round-trips as the shipped one rather than as off.
                 ** AN EDIT FORM DOES NOT ONLY DISPLAY A RECORD. IT REWRITES EVERY
                    FIELD IT LOADED, INCLUDING THE ONES IT LOADED BADLY. ** */
            collectPalletCount: c.collectPalletCount ?? true,
            collectLanguage: c.collectLanguage,
            requireFoodSafetyAck: c.requireFoodSafetyAck,
            foodSafetyWordingVer: c.foodSafetyWordingVer,
            foodSafetyText: c.foodSafetyText,
            dlCaptureMode: c.dlCaptureMode,
            dlRetentionDays: c.dlRetentionDays,
            autoArriveOnConfident: c.autoArriveOnConfident,
            requireDoorOnArrive: c.requireDoorOnArrive,
            autoReleaseDoorOnDeparted: c.autoReleaseDoorOnDeparted,
            walkInCreatesRequest: c.walkInCreatesRequest,
            maxCheckInsPerMinute: c.maxCheckInsPerMinute,
            duplicateWindowMinutes: c.duplicateWindowMinutes,
            idleResetSeconds: c.idleResetSeconds,
            /* ⚠️ `?? null`, NOT `?? someNumber`. An older payload that does not
               carry these must read as "not set", not as a value this screen
               invented and would then save. */
            idleVehicleSeconds: c.idleVehicleSeconds ?? null,
            idleSafetySeconds: c.idleSafetySeconds ?? null,
            doneResetSeconds: c.doneResetSeconds ?? null,
            kioskTruckDays: c.kioskTruckDays ?? 2,
            doorIdleAlertMinutes: c.doorIdleAlertMinutes ?? 15,
        });
        setEditing(c.checkInConfigId as number);
        setNotice(null);
        setError(null);
    }

    /**
     * The saved wording is what a driver's acknowledgement points at. Editing it
     * without bumping the version would make every row already signed claim
     * agreement to words that did not exist at the time, so the two fields move
     * together — here, and again on the server.
     */
    const original = useMemo(
        () => rows.find(r => r.checkInConfigId === editing) ?? null,
        [rows, editing],
    );
    const wordingChanged = editing !== 'new' && original !== null
        && (draft.foodSafetyText ?? '') !== (original.foodSafetyText ?? '');
    const versionChanged = editing !== 'new' && original !== null
        && draft.foodSafetyWordingVer !== original.foodSafetyWordingVer;
    const needsVersionBump = wordingChanged && !versionChanged;

    async function save() {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const token = await getToken();
            const body: CheckInConfigPayload = {
                ...draft,
                configKey: draft.configKey.trim().toLowerCase(),
                foodSafetyText: draft.foodSafetyText?.trim() ? draft.foodSafetyText.trim() : null,
            };
            const { configs } = editing === 'new'
                ? await createCheckInConfig(token, body)
                : await updateCheckInConfig(token, editing as number, body);
            setRows(configs);
            setEditing(null);
            setNotice(`Saved “${body.displayName}”. A tablet picks it up the next time it appointments.`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed.');
        } finally {
            setBusy(false);
        }
    }

    async function remove(c: CheckInConfigDto) {
        if (deleteArmed !== c.checkInConfigId) { setDeleteArmed(c.checkInConfigId); return; }
        setBusy(true);
        setError(null);
        try {
            const { configs } = await deleteCheckInConfig(await getToken(), c.checkInConfigId as number);
            setRows(configs);
            setNotice(`Deleted “${c.displayName}”. Any tablet still pointed at it falls back to the site default.`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delete failed.');
        } finally {
            setBusy(false);
            setDeleteArmed(null);
        }
    }

    async function copyUrl(key: string) {
        const url = checkInUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setCopied(key);
            window.setTimeout(() => setCopied(null), 2000);
        } catch {
            setError(`Could not copy. The URL is ${url}`);
        }
    }

    const grouped = useMemo(() => {
        const m = new Map<string, CheckInConfigDto[]>();
        for (const r of rows) {
            const k = r.facilityCode ?? '—';
            m.set(k, [...(m.get(k) ?? []), r]);
        }
        return [...m.entries()].sort((a, b) => facilityName(a[0]).localeCompare(facilityName(b[0])));
    }, [rows, facilityName]);

    if (loading) return <div className="text-sm text-muted-foreground">Loading check-in settings…</div>;

    const num = (v: string, min: number, max: number, fallback: number) => {
        const n = Number.parseInt(v, 10);
        if (!Number.isFinite(n)) return fallback;
        return Math.min(max, Math.max(min, n));
    };

    /**
     * ⭐ #73 — THE SAME CLAMP, BUT AN EMPTY BOX IS AN ANSWER.
     *
     * ⛔ `num` CANNOT SERVE THESE. It takes a fallback and therefore always
     * returns a number, so clearing the field would silently write whatever the
     * fallback happened to be — turning "no override" into an override the site
     * never set, which is the one outcome these three columns exist to avoid.
     *   ** A HELPER THAT CANNOT RETURN "NOTHING" WILL INVENT SOMETHING. **
     *
     * ⚠️ WHITESPACE COUNTS AS EMPTY. A box holding a space is a box somebody
     * cleared.
     */
    const numOrBlank = (v: string, min: number, max: number): number | null => {
        if (v.trim() === '') return null;
        const n = Number.parseInt(v, 10);
        if (!Number.isFinite(n)) return null;
        return Math.min(max, Math.max(min, n));
    };

    const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
        <div className="rounded-md border p-3 space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</div>
            {children}
        </div>
    );

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-base font-medium">Driver check-in</h3>
                <p className="text-sm text-muted-foreground mt-1">
                    One row per check-in tablet — copy the URL and open it on that tablet. A site&apos;s{' '}
                    <strong>default</strong> is what a plain <code>/checkin</code> uses. A site with no row still
                    works — the tablet runs the shipped settings. Changes take effect the next time a tablet
                    appointments; nothing here needs a deploy.
                </p>
            </div>

            <div className="rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <strong>Driver&apos;s license photo is not captured yet.</strong> The setting below is saved and
                the columns exist, but there is no upload path in the tablet page, the endpoint, or storage — so
                setting it to <em>Require a photo</em> enforces nothing today. Everything else on this screen is
                live.
            </div>

            {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                </div>
            )}
            {notice && !error && (
                <div className="rounded-md border border-emerald-600/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                    {notice}
                </div>
            )}

            {editing === null && (
                <button
                    type="button"
                    onClick={startNew}
                    disabled={facilities.length === 0}
                    className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                >
                    Add a check-in tablet
                </button>
            )}

            {editing !== null && (
                <div className="rounded-lg border p-4 space-y-4">
                    <div className="text-sm font-medium">
                        {editing === 'new' ? 'New check-in tablet' : `Editing “${draft.displayName}”`}
                    </div>

                    <Section title="Which tablet">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">Facility</span>
                                <select
                                    value={draft.facilityCode}
                                    onChange={(e) => setDraft({ ...draft, facilityCode: e.target.value })}
                                    disabled={editing !== 'new'}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm disabled:opacity-60"
                                >
                                    {facilities.map((f) => (
                                        <option key={f.FacilityCode} value={f.FacilityCode}>
                                            {f.DisplayName} ({f.FacilityCode})
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Name — what this tablet is called
                                </span>
                                <input
                                    value={draft.displayName}
                                    onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                                    placeholder="Brighton driver check-in"
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                            </label>

                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    URL key — the <code>?cfg=</code> value
                                </span>
                                <input
                                    value={draft.configKey}
                                    onChange={(e) => setDraft({ ...draft, configKey: e.target.value })}
                                    placeholder="gate"
                                    className="w-full rounded-md border px-2 py-1.5 text-sm font-mono"
                                />
                            </label>

                            <div className="grid grid-cols-1 gap-2">
                                {([
                                    ['isDefault', 'This site’s default tablet (a plain /checkin uses this one)'],
                                    ['isActive', 'Active'],
                                ] as const).map(([field, label]) => (
                                    <label key={field} className="flex items-start gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={draft[field]}
                                            onChange={(e) => setDraft({ ...draft, [field]: e.target.checked })}
                                            className="mt-0.5"
                                        />
                                        <span>{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </Section>

                    <Section title="How a driver is matched to an appointment">
                        <p className="text-xs text-muted-foreground">
                            The tablet tries these in order and stops at the first one that finds something.
                            <strong> Trust</strong> means we attach the truck to that appointment with nobody
                            checking — so only tick it for something that identifies one specific load.
                        </p>
                        <div className="space-y-1">
                            {order.map((id, i) => {
                                const s = SIGNALS.find(x => x.id === id)!;
                                return (
                                    <div key={id} className="flex items-center gap-2 rounded border px-2 py-1.5 text-sm">
                                        <span className="w-5 text-xs text-muted-foreground">{i + 1}.</span>
                                        <span className="flex-1">
                                            {s.label}
                                            <span className="block text-xs text-muted-foreground">{s.hint}</span>
                                        </span>
                                        <label className="flex items-center gap-1 text-xs">
                                            <input
                                                type="checkbox"
                                                checked={trusted.includes(id)}
                                                onChange={(e) => toggleTrust(id, e.target.checked)}
                                            />
                                            Trust
                                        </label>
                                        <button type="button" onClick={() => move(id, -1)} disabled={i === 0}
                                            className="rounded border px-1.5 text-xs disabled:opacity-30" title="Move up">↑</button>
                                        <button type="button" onClick={() => move(id, 1)} disabled={i === order.length - 1}
                                            className="rounded border px-1.5 text-xs disabled:opacity-30" title="Move down">↓</button>
                                        <button type="button" onClick={() => toggleUse(id, false)}
                                            className="rounded border px-1.5 text-xs" title="Stop using this">✕</button>
                                    </div>
                                );
                            })}
                            {order.length === 0 && (
                                <p className="text-xs text-destructive">
                                    Nothing is being matched on — every driver will look like a walk-up.
                                </p>
                            )}
                            {unused.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <span className="text-xs text-muted-foreground">Not used:</span>
                                    {unused.map(id => (
                                        <button key={id} type="button" onClick={() => toggleUse(id, true)}
                                            className="rounded border px-2 py-0.5 text-xs">
                                            + {SIGNALS.find(x => x.id === id)!.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Phone digits required
                                </span>
                                <input
                                    type="number" min={6} max={15} value={draft.phoneMinDigits}
                                    onChange={(e) => {
                                        const v = num(e.target.value, 6, 15, 10);
                                        setDraft(d => ({ ...d, phoneMinDigits: v, phoneMatchDigits: Math.min(v, d.phoneMatchDigits) }));
                                    }}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Fewer than this and we match nothing at all, so a driver who types the last
                                    four digits can&apos;t land on someone else&apos;s load.
                                </span>
                            </label>

                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Digits compared
                                </span>
                                <input
                                    type="number" min={6} max={draft.phoneMinDigits} value={draft.phoneMatchDigits}
                                    onChange={(e) => setDraft({ ...draft, phoneMatchDigits: num(e.target.value, 6, draft.phoneMinDigits, 10) })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Compares the last N digits, so +1 312 952 0484 and 312-952-0484 are the same
                                    driver. Never more than the number required.
                                </span>
                            </label>

                            <label className="text-sm sm:col-span-2">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    When one driver matches two or more of today&apos;s appointments
                                </span>
                                <select
                                    value={draft.multiMatchBehavior}
                                    onChange={(e) => setDraft({ ...draft, multiMatchBehavior: e.target.value as CheckInConfigDto['multiMatchBehavior'] })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                >
                                    <option value="ask">Ask a scheduler (recommended)</option>
                                    <option value="earliest">Take the earliest scheduled one</option>
                                </select>
                                <span className="block text-xs text-muted-foreground mt-1">
                                    A driver running twice in a day is normal. “Earliest” still asks when two are
                                    booked for the same time or one has no time yet.
                                </span>
                            </label>
                        </div>
                    </Section>

                    {/**
                      * ⛔ "First language" WAS A TOGGLE HERE AND IS DELIBERATELY NOT ONE NOW.
                      *
                      * Pete, 2026-08-25: *"asking them that everytime kinda is a waste of
                      * their time right? Lets drop the preferred language for now."*
                      *
                      * ⚠️ AND A TOGGLE THAT NO LONGER CHANGES ANYTHING IS WORSE THAN A
                      * MISSING ONE — the same fault as the "Scan paperwork" button that
                      * only closed its own dialog. Somebody would switch it on, watch the
                      * tablet ignore it, and go looking for a bug.
                      *
                      * ⭐ THE COLUMN AND THE STORED VALUES SURVIVE. Historic check-ins keep
                      * the language they recorded; this is a question the kiosk stopped
                      * asking, not data being destroyed.
                      */}
                    <Section title="What the tablet asks the driver for">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {([
                                /**
                                 * ⛔⛤ ~~'Container number'~~ / ~~'Trailer number'~~ — RELABELLED
                                 * 2026-09-23, because the switch's MEANING CHANGED and the
                                 * words did not.
                                 *
                                 * Until #73 these asked for an optional number. They now put a
                                 * REQUIRED field in front of the driver: he cannot finish
                                 * check-in without a trailer, container or plate number, and if
                                 * he has none he types the bypass code, which raises an alert on
                                 * his card for a scheduler to chase.
                                 *   ** WHEN A SWITCH STARTS DOING SOMETHING ELSE, ITS LABEL IS
                                 *      PART OF THE CHANGE. A SETTING NAMED FOR ITS OLD
                                 *      BEHAVIOUR IS A TRAP FOR THE PERSON WHO SET IT. **
                                 *
                                 * ⚠️ THE HINT SAYS "EITHER ONE" BECAUSE THAT IS THE WHOLE
                                 * SUBTLETY: switching BOTH on does not demand both numbers. One
                                 * field of the three satisfies it, and a site reading these as
                                 * two separate requirements would turn a truck away for a box
                                 * that has only a plate.
                                 *
                                 * ⭐ `PO number` IS UNCHANGED. Nothing gates on it.
                                 */
                                ['collectContainerNumber', 'Container number — required',
                                 'Asks for a container, trailer or plate number and will not let him past without one.'],
                                ['collectTrailerNumber', 'Trailer number — required',
                                 'Either one of these switches asks the same question; he only has to answer it once.'],
                                ['collectPoNumber', 'PO number', null],
                                /* ---- ⭐⭐ THE SWITCH THAT EXISTED FOR SIX WEEKS WITH NO SCREEN --
                                 *
                                 * ⛔⛤⛤ Migration 192 made `CollectPalletCount` in August and
                                 * NOTHING EVER SET IT. The column had a default, the route
                                 * parsed it, the payload type demanded it — and there was no
                                 * checkbox here and no line in the repository, so the value a
                                 * site actually wanted could not be expressed and the value it
                                 * had could not be seen.
                                 *   ** A SETTING WITH NO SCREEN IS A COLUMN, NOT A SETTING. **
                                 *
                                 * ⚠️ THIS ONE IS DIFFERENT FROM THE THREE ABOVE: it does not
                                 * gate anything. Skipping is allowed, and 0 is a real answer
                                 * meaning "no pallets", so switching it on adds a step the
                                 * driver may pass through without typing anything. It will
                                 * never turn a truck away.
                                 */
                                ['collectPalletCount', 'Pallet count',
                                 'Asks the driver how many pallets are on board. He may skip it, and 0 is a valid answer.'],
                            ] as const).map(([field, label, hint]) => (
                                <label key={field} className="flex items-start gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={draft[field]}
                                        onChange={(e) => setDraft({ ...draft, [field]: e.target.checked })}
                                        className="mt-0.5"
                                    />
                                    <span>
                                        {label}
                                        {hint && (
                                            <span className="block text-xs text-muted-foreground">{hint}</span>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </Section>

                    <Section title="Food &amp; safety acknowledgement">
                        <label className="flex items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={draft.requireFoodSafetyAck}
                                onChange={(e) => setDraft({ ...draft, requireFoodSafetyAck: e.target.checked })}
                                className="mt-0.5"
                            />
                            <span>Ask the driver to acknowledge before checking in</span>
                        </label>
                        <label className="text-sm block">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Wording — leave blank to use the shipped text
                            </span>
                            <textarea
                                rows={3}
                                value={draft.foodSafetyText ?? ''}
                                onChange={(e) => setDraft({ ...draft, foodSafetyText: e.target.value })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                        </label>
                        <label className="text-sm block sm:w-48">
                            <span className="block text-xs text-muted-foreground mb-1">Version</span>
                            <input
                                value={draft.foodSafetyWordingVer}
                                onChange={(e) => setDraft({ ...draft, foodSafetyWordingVer: e.target.value })}
                                placeholder="v1"
                                className="w-full rounded-md border px-2 py-1.5 text-sm font-mono"
                            />
                        </label>
                        {needsVersionBump && (
                            <p className="rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                You changed the wording but not the version. Every driver who has already agreed
                                is recorded against <code>{original?.foodSafetyWordingVer}</code> — leave the
                                version alone and their record will point at words they never saw. Bump it.
                            </p>
                        )}
                    </Section>

                    <Section title="Driver’s license photo">
                        <label className="text-sm block">
                            <span className="block text-xs text-muted-foreground mb-1">Capture</span>
                            <select
                                value={draft.dlCaptureMode}
                                onChange={(e) => setDraft({ ...draft, dlCaptureMode: e.target.value as CheckInConfigDto['dlCaptureMode'] })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            >
                                {DL_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                            {draft.dlCaptureMode === 'required' && (
                                <span className="block text-xs text-amber-700 mt-1">
                                    A broken camera then stops drivers checking in.
                                </span>
                            )}
                        </label>
                        <label className="text-sm block sm:w-64">
                            <span className="block text-xs text-muted-foreground mb-1">
                                Delete photos after (days) — blank keeps them
                            </span>
                            <input
                                type="number" min={1} max={3650}
                                value={draft.dlRetentionDays ?? ''}
                                onChange={(e) => setDraft({
                                    ...draft,
                                    dlRetentionDays: e.target.value.trim() === '' ? null : num(e.target.value, 1, 3650, 365),
                                })}
                                className="w-full rounded-md border px-2 py-1.5 text-sm"
                            />
                            <span className="block text-xs text-muted-foreground mt-1">
                                A license shows a date of birth and a home address. Nothing deletes these today —
                                set a number here when someone has decided how long we should hold them.
                            </span>
                        </label>
                    </Section>

                    <Section title="What checking in does">
                        <div className="grid grid-cols-1 gap-2">
                            {([
                                ['autoArriveOnConfident', 'Mark the appointment ARRIVED automatically on a trusted match'],
                                ['requireDoorOnArrive', 'Require a dock door to be assigned before arriving'],
                                ['autoReleaseDoorOnDeparted', 'Free the door automatically when the truck is marked departed'],
                                ['walkInCreatesRequest', 'A driver with no booking raises a REQUESTED appointment for a scheduler to approve'],
                            ] as const).map(([field, label]) => (
                                <label key={field} className="flex items-start gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={draft[field]}
                                        onChange={(e) => setDraft({ ...draft, [field]: e.target.checked })}
                                        className="mt-0.5"
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </Section>

                    <Section title="Tablet behavior">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Clear the screen after (seconds idle)
                                </span>
                                <input
                                    type="number" min={10} max={3600} value={draft.idleResetSeconds}
                                    onChange={(e) => setDraft({ ...draft, idleResetSeconds: num(e.target.value, 10, 3600, 90) })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    So the next driver never sees the last one&apos;s details.
                                    Used on every screen unless one below overrides it.
                                </span>
                            </label>

                            {/**
                              * ---- ⭐⭐ #73 / MIGRATION 191 — A TIMEOUT PER SCREEN -------
                              *
                              * > Pete, 2026-09-23: *"We should have a timeout on each screen
                              * > right? For the saftey screen, longer, for the final screen
                              * > short. But not hardcoded"* — and, on the general setting
                              * reading 90 while the last screen cleared in five: *"Is that set
                              * somewhere else?"* It was: a constant in `DriverCheckIn.tsx`.
                              *   ** A CONSTANT WITH A COMMENT CALLING IT A DEFAULT IS STILL A
                              *      CONSTANT. THE TEST IS WHETHER ANYONE BUT A DEPLOY CAN
                              *      CHANGE IT. **
                              *
                              * ⛔ BLANK IS THE MEANINGFUL STATE and each box says so in its
                              * placeholder. Leaving all three empty is exactly today's
                              * behaviour, which is why they are nullable rather than defaulted.
                              *
                              * ⚠️ THE HINTS EXPLAIN THE DIRECTION, not the number. A site
                              * that does not know WHY the safety screen wants LONGER will set
                              * it shorter to be tidy, and reset a driver halfway through a
                              * paragraph in his second language.
                              */}
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Trailer / container screen (seconds)
                                </span>
                                <input
                                    type="number" min={10} max={3600} placeholder="use the general one"
                                    value={draft.idleVehicleSeconds ?? ''}
                                    onChange={(e) => setDraft({
                                        ...draft, idleVehicleSeconds: numOrBlank(e.target.value, 10, 3600),
                                    })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Usually SHORTER — about 30. He may walk out to read the number off
                                    the box, and must not come back to his own half-filled screen.
                                </span>
                            </label>

                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Safety notice screen (seconds)
                                </span>
                                <input
                                    type="number" min={10} max={3600} placeholder="use the general one"
                                    value={draft.idleSafetySeconds ?? ''}
                                    onChange={(e) => setDraft({
                                        ...draft, idleSafetySeconds: numOrBlank(e.target.value, 10, 3600),
                                    })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Usually LONGER. Reading slowly and walking away look identical from
                                    here, and this is the screen a driver is most likely to be reading.
                                </span>
                            </label>

                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Confirmation screen (seconds)
                                </span>
                                <input
                                    type="number" min={3} max={3600} placeholder="5"
                                    value={draft.doneResetSeconds ?? ''}
                                    onChange={(e) => setDraft({
                                        ...draft, doneResetSeconds: numOrBlank(e.target.value, 3, 3600),
                                    })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Short. Raise it if drivers need time to photograph their arrival
                                    number. ⚠️ Blank uses 5 — NOT the general timeout.
                                </span>
                            </label>
                            {/**
                              * ⭐⭐ THE FLOOR KIOSK'S DAY WINDOW (#309, migration 168).
                              *
                              * Pete: *"Probably should only show todays and yesterdays items
                              * but lets put this in the config screen. More than a couple days
                              * would confuse the fork lift drivers doing the labor."*
                              *
                              * ⚠️ IT IS A DIFFERENT KIOSK FROM THE ONE THIS SCREEN IS NAMED
                              * AFTER, and the label says so rather than leaving somebody to
                              * discover it. The setting lives here because this is already the
                              * per-facility kiosk table — see the migration for the reasoning.
                              *
                              * ⛔ THE CAP IS 7 AND IT IS NOT A UI PREFERENCE. The same bound is
                              * in the repository clamp and in a CHECK constraint; widening it
                              * here alone would produce a save that fails with a SQL error
                              * this screen cannot explain.
                              */}
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Labor kiosk shows (days)
                                </span>
                                <input
                                    type="number" min={1} max={7} value={draft.kioskTruckDays}
                                    onChange={(e) => setDraft({ ...draft, kioskTruckDays: num(e.target.value, 1, 7, 2) })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    laborclock.symbia.com. 2 = today and yesterday. More than a
                                    couple of days and a forklift driver is picking from a list
                                    of trucks that have gone.
                                </span>
                            </label>
                            {/**
                              * ---- ⭐⭐ THE DOOR ALERT'S THRESHOLD (migration 218) ------
                              *
                              * > Pete: *"the threshold set by the facility for time Allowed
                              * > at a DOck door before activty begins... So a truck doesn't
                              * > sit for more than 15 minutes before someone is working
                              * > it."*
                              *
                              * ⛔⛤ IT IS ON THIS SCREEN IN THE SAME CHANGE THAT ADDED THE
                              * COLUMN, because migration 191's three timeouts were not and
                              * stayed SQL-only — the exact failure that migration existed
                              * to prevent.
                              *   ** A SETTING WITH NO SCREEN IS A COLUMN, NOT A SETTING. **
                              *
                              * ⚠️ min=0 AND THE HELP TEXT SAYS WHAT 0 DOES. A number box
                              * that accepts a value whose meaning is not written down is a
                              * trap for whoever types it.
                              */}
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Alert if a truck sits at a door (minutes)
                                </span>
                                <input
                                    type="number" min={0} max={1440} value={draft.doorIdleAlertMinutes}
                                    onChange={(e) => setDraft({ ...draft, doorIdleAlertMinutes: num(e.target.value, 0, 1440, 15) })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    The floor board blinks a red band across a truck that has had
                                    a dock door this long with nobody working it. <b>0 turns the
                                    alert off</b> for this facility.
                                </span>
                            </label>
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Ignore a repeat from the same phone for (minutes)
                                </span>
                                <input
                                    type="number" min={0} max={240} value={draft.duplicateWindowMinutes}
                                    onChange={(e) => setDraft({ ...draft, duplicateWindowMinutes: num(e.target.value, 0, 240, 10) })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Stops a double-tap becoming a second truck in the arrival order. 0 turns it off.
                                </span>
                            </label>
                            <label className="text-sm">
                                <span className="block text-xs text-muted-foreground mb-1">
                                    Most check-ins a minute
                                </span>
                                <input
                                    type="number" min={1} max={600} value={draft.maxCheckInsPerMinute}
                                    onChange={(e) => setDraft({ ...draft, maxCheckInsPerMinute: num(e.target.value, 1, 600, 12) })}
                                    className="w-full rounded-md border px-2 py-1.5 text-sm"
                                />
                                <span className="block text-xs text-muted-foreground mt-1">
                                    A ceiling, not a target. The check-in page has no login, so this is what stops
                                    a stuck tablet — or someone having fun — filling the day with junk arrivals.
                                </span>
                            </label>
                        </div>
                    </Section>

                    {draft.configKey.trim() && (
                        <p className="text-xs text-muted-foreground">
                            This tablet is at{' '}
                            <code className="font-mono">{checkInUrl(draft.configKey.trim().toLowerCase())}</code>
                        </p>
                    )}

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => void save()}
                            disabled={busy || !draft.facilityCode || !draft.configKey.trim() || !draft.displayName.trim()}
                            className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                        >
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setEditing(null); setError(null); }}
                            disabled={busy}
                            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    No check-in tablets configured yet. Every gate would run on the shipped settings — match on
                    cell phone first, then container, then our reference; never attach a truck on a PO alone;
                    ask a scheduler when a driver matches two appointments.
                </p>
            ) : (
                <div className="space-y-5">
                    {grouped.map(([code, configs]) => (
                        <div key={code}>
                            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                                {facilityName(code)}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                                            <th className="py-2 pr-4 font-medium">Tablet</th>
                                            <th className="py-2 pr-4 font-medium">Matching</th>
                                            <th className="py-2 pr-4 font-medium">Gate</th>
                                            <th className="py-2 pr-4 font-medium">URL</th>
                                            <th className="py-2 pr-4 font-medium" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {configs.map((c) => (
                                            <tr key={c.checkInConfigId} className="border-b last:border-0 align-top">
                                                <td className="py-2 pr-4">
                                                    <div className="font-medium">
                                                        {c.displayName}
                                                        {c.isDefault && (
                                                            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">
                                                                DEFAULT
                                                            </span>
                                                        )}
                                                        {!c.isActive && (
                                                            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                                                off
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground font-mono">{c.configKey}</div>
                                                </td>
                                                <td className="py-2 pr-4 text-xs text-muted-foreground">
                                                    {parseList(c.matchSignalOrder).map(s => SIGNALS.find(x => x.id === s)!.label).join(' → ') || 'nothing'}
                                                    <br />
                                                    trusted: {parseList(c.confidentSignals).length
                                                        ? parseList(c.confidentSignals).map(s => SIGNALS.find(x => x.id === s)!.label).join(', ')
                                                        : 'none — a human sees every truck'}
                                                    <br />
                                                    ties: {c.multiMatchBehavior === 'earliest' ? 'earliest scheduled' : 'ask a scheduler'}
                                                </td>
                                                <td className="py-2 pr-4 text-xs text-muted-foreground">
                                                    license: {c.dlCaptureMode}
                                                    {c.dlRetentionDays ? ` · ${c.dlRetentionDays}d` : ' · kept'}
                                                    <br />
                                                    {c.requireFoodSafetyAck ? `food safety ${c.foodSafetyWordingVer}` : 'no acknowledgement'}
                                                    <br />
                                                    {[
                                                        c.autoArriveOnConfident && 'auto-arrive',
                                                        c.requireDoorOnArrive && 'door required',
                                                        c.walkInCreatesRequest && 'walk-ups requested',
                                                    ].filter(Boolean).join(' · ') || 'record only'}
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => void copyUrl(c.configKey ?? '')}
                                                        className="text-xs underline decoration-dotted"
                                                        title={checkInUrl(c.configKey ?? '')}
                                                    >
                                                        {copied === c.configKey ? 'Copied!' : 'Copy URL'}
                                                    </button>
                                                </td>
                                                <td className="py-2 pr-4 text-right whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(c)}
                                                        disabled={busy}
                                                        className="rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-40"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => void remove(c)}
                                                        disabled={busy}
                                                        className="ml-2 rounded-md border px-2 py-1 text-xs font-medium text-destructive disabled:opacity-40"
                                                    >
                                                        {deleteArmed === c.checkInConfigId ? 'Really delete?' : 'Delete'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                A tablet pointed at a row that has been deleted or switched off does not
                stop working — it falls back to this site&apos;s default, and to the shipped settings if there
                isn&apos;t one. The tablet is trusted by its network address, not by a login, so it never learns
                any of the matching rules above: it sends what the driver typed and the server decides.
            </p>
        </div>
    );
}
